import { useAuthStore } from '@tundr/auth';
import { useLocale } from '@tundr/i18n';
import {
  clearSentryExtra,
  clearSentryUser,
  setSentryExtra,
  setSentryUser,
} from '@tundr/observability';
import { watch } from 'vue';
import { ExternalService, isServiceEnabled } from '../external-services';
import { isInternalHrUserByEmail } from '../../utils/hr/hr.utils';
import {
  identifyTracking,
  initAmplitudeEngagement,
  isTrackableCompany,
  resetTracking,
  trackAmplitudeEvent,
} from './amplitude';
import { TRACKING_EVENTS } from './tracking-events';

const SENTRY_COMPANY_EXTRA_KEYS = [
  'companyId',
  'companyName',
  'companyPlan',
  'companyChannel',
] as const;

const clearSentryTrackingContext = () => {
  clearSentryUser();
  SENTRY_COMPANY_EXTRA_KEYS.forEach(clearSentryExtra);
};

export const useTrackingSync = () => {
  const authStore = useAuthStore();
  const locale = useLocale();

  // A login completes at the first company selection: the tracking identity is
  // the company hrId, which does not exist before that point.
  let isLoginPending = false;

  authStore.$onAction(({ name, after }) => {
    if (name === 'setAuth') {
      after(() => {
        isLoginPending = !authStore.selectedCompany;
      });
    }

    if (name === 'setCompanySelected') {
      after(() => {
        const company = authStore.selectedCompany;
        if (!isTrackableCompany(company)) return;

        const isLoginCompletion = isLoginPending;
        isLoginPending = false;

        if (isLoginCompletion) {
          trackAmplitudeEvent(TRACKING_EVENTS.USER_LOGGED_IN, {
            loginDate: new Date().toISOString(),
            userId: company.hrId,
          });
        }

        trackAmplitudeEvent(TRACKING_EVENTS.USER_SELECTED_COMPANY, {
          selectedCompanyDate: new Date().toISOString(),
          userCompanyPlan: company.currentWelfarePlan,
          userCompanyChannel: company.companyChannel?.name,
          userCompanyId: company.id,
          userCompanyName: company.name,
          isInternalHrUser: isInternalHrUserByEmail(company.email),
        });
      });
    }
  });

  // flush: 'sync' keeps the identity update ahead of the intent events tracked
  // in the $onAction after callbacks, so those events carry the right user id.
  watch(
    [() => authStore.authData, () => authStore.selectedCompany],
    ([authData, company], [previousAuthData]) => {
      if (!authData) {
        if (previousAuthData) {
          resetTracking();
          if (isServiceEnabled(ExternalService.Sentry)) {
            clearSentryTrackingContext();
          }
        }
        return;
      }

      identifyTracking({ hasSession: true, company });

      if (!isTrackableCompany(company)) {
        if (isServiceEnabled(ExternalService.Sentry)) {
          clearSentryTrackingContext();
        }
        return;
      }

      initAmplitudeEngagement({
        userId: company.hrId,
        companyId: company.id,
        companyName: company.name,
        language: locale.locale.value,
      });

      if (isServiceEnabled(ExternalService.Sentry)) {
        const user = authData.user;
        if (user) {
          setSentryUser(company.hrId, `${user.firstName} ${user.lastName}`);
        }

        setSentryExtra('companyId', company.id);
        setSentryExtra('companyName', company.name);
        setSentryExtra('companyPlan', company.currentWelfarePlan);
        setSentryExtra('companyChannel', company.companyChannel?.name);
      }
    },
    { immediate: true, flush: 'sync' },
  );
};
